import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import {
  getListingFieldsForDisplay,
} from 'common/listingHelpers';

import {
  numberWithCommas,
  bedsDisplayString,
  bathsDisplayString,
} from 'common/formatters';

import {
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_WITHDRAWN,
} from 'common/dataTableConstants';

import {
  dataTable,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';

import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_GREY,
} from 'client/components/SrPartnerListingCard';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';

import {
  MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
} from 'common/marketingPartnerListingConstants';

export function getDataTableSettingsFromProps(props) {
  const rootViewId = props.marketingListingsViewId || DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_WITHDRAWN;

  const viewId = lodashCompact([
    rootViewId,
    props.partner ? `partner-id-${props.partner.id}` : '',
    props.listingId ? `listing-id-${props.listingId}` : '',
  ]).join('_');

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: viewId,
    filter: {
      'marketingPartnerListings.status': [
        MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
      ],
    },
    sort: {
      withdrawnDate: props.sortOrder || 'desc',
    },
    count: 30,
  };

  if (props.partner) {
    dataTableSettings.filter['marketingPartnerListings.partnerId'] = props.partner.id;
  }
  return dataTableSettings;
}

@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
@branch()
class SrDataTablePartnerHouseholdWithdrawns extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    items: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    withBlankState: PropTypes.bool,
    listingId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
  }

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(
        actionDataTableLoadNextPage,
        getDataTableSettingsFromProps(this.props),
      );
    }
  }

  _getBottomChips = (listing) => {
    const label = [
      `${listing.addressCity},`,
      listing.addressStateCode,
    ].join(' ');

    return [
      {
        label,
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
    ];
  }

  render() {
    const {
      items: withdrawnProperties,
      onInfiniteScrollWrapperRef,
      dataTableIsFullyLoaded,
      dataTableApiState,
      withBlankState,
    } = this.props;

    if (dataTableIsFullyLoaded && !withBlankState && withdrawnProperties.length === 0) {
      return null;
    }

    return (
      <div className='SrDataTablePartnerHouseholdWithdrawns' ref={onInfiniteScrollWrapperRef}>
        <SrPartnerSection
          title='Withdrawn Listings (Admin Only)'
        >
          <SrPartnerList>
            {dataTableIsFullyLoaded && withdrawnProperties.length === 0 && (
              <SrDataRetrievalText
                text='No properties have been withdrawn so far.'
              />
            )}
            {withdrawnProperties.map((withdrawnProperty) => {
              const itemKey = [
                withdrawnProperty.householdId,
                withdrawnProperty.listingId,
                withdrawnProperty.partnerId,
              ].join('-');

              const listingFieldsForDisplay = getListingFieldsForDisplay({
                listing: withdrawnProperty,
              });

              const stats = [
                `$${numberWithCommas(listingFieldsForDisplay.price)}`,
                bedsDisplayString(listingFieldsForDisplay),
                bathsDisplayString(listingFieldsForDisplay),
                `${numberWithCommas(listingFieldsForDisplay.squareFeet)} ft`,
              ];

              const daysOnMarketText = typeof withdrawnProperty.daysOnMarketBeforeWithdrawn === 'number'
                ? pluralize('day', withdrawnProperty.daysOnMarketBeforeWithdrawn, true)
                : null;

              return (
                <SrPartnerListingCard
                  key={itemKey}
                  to={`/partner/${withdrawnProperty.partner.slug}/listing/${withdrawnProperty.listingId}`}
                  photos={[withdrawnProperty.listing.sunroomPhotoUrls[0]]}
                  tags={[
                    {
                      theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_GREY,
                      label: moment(withdrawnProperty.withdrawnDate || null)
                        .format('MMM D, YYYY'),
                    },
                  ]}
                  title={withdrawnProperty.addressStreetDisplay}
                  stats={stats}
                  date={daysOnMarketText}
                  bottomChips={this._getBottomChips(withdrawnProperty.listing)}
                />
              );
            })}
          </SrPartnerList>
          {dataTableApiState.inProgress && (
            <div className='loading-ellipses' />
          )}
        </SrPartnerSection>
      </div>
    );
  }
}

SrDataTablePartnerHouseholdWithdrawns.displayName = 'SrDataTablePartnerHouseholdWithdrawns';

export default SrDataTablePartnerHouseholdWithdrawns;
